/**
 * R80 · Public read-only Package preview.
 * ----------------------------------------------------------------
 * Accessed via `/pkg/view/:token`. Uses the unauthenticated
 * GET /api/v1/public/packages/{token} endpoint which redacts all money
 * fields. No app chrome — client-friendly proposal layout only.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ROOT_API } from "@/lib/api";
import { SmartRichTextDisplay } from "@/components/SmartRichText";
import { FREQ_LABELS } from "@/pages/app/Packages";
import { ChannelIcons } from "@/components/packages/channels";
import { Button } from "@/components/ui/button";
import { ClipboardCopy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AP_STYLE = {
  approved: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  rejected: { bg: "#ffe4e6", fg: "#9f1239", border: "#fda4af" },
  pending:  { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  draft:    { bg: "#f4f4f5", fg: "#52525b", border: "#d4d4d8" },
};

export default function SharedPackageView() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${ROOT_API}/v1/public/packages/${token}`);
        setPkg(r.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "This link is invalid or has been revoked." : "Failed to load preview.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</div>;
  }
  if (error || !pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-white" data-testid="shared-pkg-error">
        <div className="max-w-md text-center">
          <div className="text-lg font-semibold text-slate-900">Preview unavailable</div>
          <div className="text-sm text-muted-foreground mt-2">{error || "This link is invalid or has expired."}</div>
        </div>
      </div>
    );
  }

  const sortedSections = [...(pkg.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const bySection = new Map();
  bySection.set(null, []);
  sortedSections.forEach((s) => bySection.set(s.id, []));
  (pkg.lines || []).forEach((l) => {
    const sid = l.section_id && bySection.has(l.section_id) ? l.section_id : null;
    bySection.get(sid).push(l);
  });
  for (const arr of bySection.values()) {
    arr.sort((a, b) => (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)));
  }
  const groupByCategory = (arr) => {
    const buckets = new Map();
    for (const l of arr) {
      const cat = l.category || "other";
      if (!buckets.has(cat)) buckets.set(cat, []);
      buckets.get(cat).push(l);
    }
    return Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };
  const previewSections = [];
  sortedSections.forEach((s) => {
    const items = bySection.get(s.id) || [];
    if (items.length) previewSections.push({ id: s.id, name: s.name, color: s.color, groups: groupByCategory(items) });
  });
  const ungrouped = bySection.get(null) || [];
  if (ungrouped.length) previewSections.push({ id: null, name: sortedSections.length > 0 ? "Other" : null, color: null, groups: groupByCategory(ungrouped) });

  const ap = AP_STYLE[pkg.approval_status || "draft"] || AP_STYLE.draft;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="shared-pkg-view">
      <div className="max-w-3xl mx-auto p-6 md:p-10">
        <div className="text-[10px] uppercase tracking-[.25em] text-slate-500 mb-1">
          {pkg.client_name}{pkg.client_name && pkg.brand_name ? " · " : ""}{pkg.brand_name}
        </div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="shared-pkg-title">{pkg.name}</h1>
        <div className="mt-1 text-sm text-slate-600">
          Period: <span className="font-mono">{pkg.start_date || "—"} → {pkg.end_date || "no end date"}</span>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-0.5 border"
            style={{ background: ap.bg, color: ap.fg, borderColor: ap.border }}
            data-testid="shared-pkg-approval"
          >{pkg.approval_status || "draft"}</span>
          {pkg.approval_date && <span className="text-xs text-slate-600">{pkg.approval_date}</span>}
          {pkg.approver_name && <span className="text-xs">· {pkg.approver_name}</span>}
          {pkg.approver_type && <span className="text-[10px] uppercase text-slate-500">({pkg.approver_type})</span>}
          <Button variant="outline" size="sm" className="rounded-none h-7 text-[11px] ml-auto no-print" onClick={() => window.print()} data-testid="shared-pkg-print">
            Print / PDF
          </Button>
          <Button
            variant="outline" size="sm" className="rounded-none h-7 text-[11px] no-print"
            onClick={async () => {
              try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
              catch { toast.error("Copy blocked"); }
            }}
            data-testid="shared-pkg-copy-link"
          >
            <ClipboardCopy className="w-3 h-3 mr-1" /> Copy link
          </Button>
        </div>
        <hr className="my-6 border-slate-200" />
        {previewSections.length === 0 && (
          <div className="text-sm italic text-slate-500">No items yet.</div>
        )}
        {previewSections.map((sec) => {
          const accent = sec.color || "#3B82F6";
          return (
            <div key={sec.id || "none"} className="mb-6" data-testid={`shared-pkg-section-${sec.id || "none"}`}>
              {sec.name && (
                <h2 className="text-sm font-bold uppercase tracking-widest border-l-4 pl-3 mb-2" style={{ color: accent, borderColor: accent }}>
                  {sec.name}
                </h2>
              )}
              {sec.groups.map(([cat, items]) => (
                <div key={cat} className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{cat}</div>
                  <table className="w-full text-sm border border-slate-200 border-collapse">
                    <thead className="bg-slate-50">
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="text-left px-3 py-1.5 border-r border-slate-200">Item</th>
                        <th className="text-right px-3 py-1.5 border-r border-slate-200 w-16">Count</th>
                        <th className="text-left px-3 py-1.5 border-r border-slate-200 w-24">Frequency</th>
                        <th className="text-left px-3 py-1.5 w-24">Channels</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((l, i) => (
                        <tr key={i} className="border-t border-slate-200">
                          <td className="px-3 py-1 border-r border-slate-200">{l.name}</td>
                          <td className="px-3 py-1 text-right font-mono border-r border-slate-200">{Number(l.count) || 0}</td>
                          <td className="px-3 py-1 text-[11px] border-r border-slate-200">{FREQ_LABELS[l.frequency] || l.frequency || "—"}</td>
                          <td className="px-3 py-1"><ChannelIcons channels={l.channels} size={14} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        })}
        {pkg.notes_html && (
          <div className="mt-6 pt-4 border-t border-slate-200" data-testid="shared-pkg-notes">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes</div>
            <div className="text-sm text-slate-700 max-w-none">
              <SmartRichTextDisplay html={pkg.notes_html} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
